import {
  BanknotesIcon,
  CheckCircleIcon,
  ChatBubbleBottomCenterTextIcon,
  EyeIcon,
  FireIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { buyerTestimonials } from '../../../config/brandSocialProof';
import { shopCategoryPathForGarmentChip } from '../../../config/shopProductCategories';

export const HOME_TRUST_BADGES = [
  {
    title: 'Secure Payments',
    description: 'Secure checkout',
    icon: ShieldCheckIcon,
    metric: 'SSL secured',
  },
  {
    title: 'Fast Shipping',
    description: 'Tracked delivery options',
    icon: TruckIcon,
    metric: 'Tracked delivery',
  },
  {
    title: 'Quality Guarantee',
    description: 'Easy returns',
    icon: SparklesIcon,
    metric: '30‑day returns',
  },
  {
    title: 'Support',
    description: 'Here when you need us',
    icon: ChatBubbleBottomCenterTextIcon,
    metric: 'Fast replies',
  },
] as const;

export const HOME_BUYER_VALUE_PROPS = [
  {
    title: 'Limited drops, not mass merch',
    body: 'Small-batch releases so your piece feels rare—not something everyone already owns.',
    icon: SparklesIcon,
  },
  {
    title: 'Preview you can trust',
    body: 'What you see in the product preview is what we aim to deliver in print and color.',
    icon: CheckCircleIcon,
  },
  {
    title: 'Premium feel, clean finishing',
    body: 'Thick fabrics, crisp edges, and prints that look intentional—not cheap POD.',
    icon: ShieldCheckIcon,
  },
  {
    title: 'Support when you need it',
    body: 'Tracked delivery options and a straightforward returns window if it’s not the vibe.',
    icon: ChatBubbleBottomCenterTextIcon,
  },
] as const;

export const HOME_BUYER_TESTIMONIALS = buyerTestimonials.slice(0, 6).map(
  ({ quote, author, location }) => ({
    quote,
    author,
    location,
  }),
);

export const HOME_AUDIENCE_EDITS = [
  {
    title: "Men's",
    description: 'Clean fits. Bold statements. Pieces with premium everyday energy.',
    cta: "Shop Men's",
    link: '/shop?edit=mens',
    image: 'https://i.pinimg.com/736x/a5/46/f4/a546f4b422d6255dd32e494afc4de63c.jpg',
    tone: 'linear-gradient(135deg, #F3E3CE 0%, #F6EBDD 55%, #F1E6D6 100%)',
    accent: '#9B59B6',
  },
  {
    title: "Women's",
    description: 'Effortless staples made to turn heads.',
    cta: "Shop Women's",
    link: '/shop?edit=womens',
    image: 'https://i.pinimg.com/736x/00/75/ba/0075baeea7997fba76291c39b022df5a.jpg',
    tone: 'linear-gradient(135deg, #EDE8E3 0%, #F2ECE8 55%, #EAE3DE 100%)',
    accent: '#3B3B98',
  },
  {
    title: 'Unisex',
    description: 'Genderless essentials with a premium fit.',
    cta: 'Shop Unisex',
    link: '/shop?edit=unisex',
    image: 'https://i.pinimg.com/736x/01/42/f3/0142f315c77d455c496c70eb9d406383.jpg',
    tone: 'linear-gradient(135deg, #E8EEF8 0%, #EEF2FF 55%, #F3F4F6 100%)',
    accent: '#F9A826',
  },
] as const;

export const HOME_SHOP_CATEGORIES = [
  {
    title: 'Signature Tees',
    description: 'Everyday staples with premium print and a clean fit.',
    cta: 'Explore Tees',
    link: shopCategoryPathForGarmentChip('tees'),
    image: 'https://i.pinimg.com/736x/bd/bc/89/bdbc893332d71b330f571ce631f8b766.jpg',
    tone: 'linear-gradient(135deg, #F3E3CE 0%, #F6EBDD 55%, #F1E6D6 100%)',
    accent: '#3B3B98',
    designsAvailableLine: 'Dozens of designs available',
  },
  {
    title: 'Premium Hoodies',
    description: 'Heavyweight comfort, soft inside, built to hold shape.',
    cta: 'Explore Hoodies',
    link: shopCategoryPathForGarmentChip('hoodies'),
    image: 'https://i.pinimg.com/736x/0c/32/e4/0c32e4720d661d9b5b0224cd3bc548b7.jpg',
    tone: 'linear-gradient(135deg, #E8EEF8 0%, #EEF2FF 55%, #F3F4F6 100%)',
    accent: '#9B59B6',
    designsAvailableLine: 'Dozens of designs available',
  },
  {
    title: 'Sweatshirts',
    description: 'Minimal layers with a bold, easy-to-style feel.',
    cta: 'Explore Sweatshirts',
    link: shopCategoryPathForGarmentChip('sweatshirts'),
    image: 'https://i.pinimg.com/736x/d3/b2/d0/d3b2d0e1f710dc555e816c6369bb0f23.jpg',
    tone: 'linear-gradient(135deg, #EDE8E3 0%, #F2ECE8 55%, #EAE3DE 100%)',
    accent: '#F9A826',
    designsAvailableLine: 'Dozens of designs available',
  },
] as const;

export const HOME_DROP_STEPS = [
  {
    step: '01',
    title: 'We release limited drops',
    body: 'Curated designs in small batches—premium, not random.',
    icon: SparklesIcon,
  },
  {
    step: '02',
    title: 'You grab your piece',
    body: 'When a drop sells out, it’s gone. No mass reprints.',
    icon: FireIcon,
  },
  {
    step: '03',
    title: 'We print and deliver',
    body: 'Made to order with care, then shipped to your door.',
    icon: TruckIcon,
  },
] as const;

export const HOME_FAQ_GROUPS = [
  {
    id: 'drops',
    label: 'Drops',
    icon: FireIcon,
    items: [
      {
        q: 'How do drops work?',
        a: 'Limited releases in small batches. When a drop sells out, it’s gone—no mass reprints.',
      },
      {
        q: 'Can I create my own design?',
        a: 'Yes—use the design tool anytime, or explore creator options to share designs.',
      },
    ],
  },
  {
    id: 'shipping',
    label: 'Shipping',
    icon: TruckIcon,
    items: [
      {
        q: 'How long does delivery take?',
        a: 'Morocco: 3–7 business days. International: typically 7–14 days with tracking where available.',
      },
      {
        q: 'Do you ship internationally?',
        a: 'Yes—worldwide delivery with tracked options where available.',
      },
    ],
  },
  {
    id: 'fit',
    label: 'Fit',
    icon: EyeIcon,
    items: [
      {
        q: 'How does sizing work?',
        a: 'Fit notes and size guides on every product. Between sizes? Size up for a relaxed fit.',
      },
      {
        q: 'Will the print match the preview?',
        a: 'Previews aim to match production. Slight variation can happen by fabric and color—contact us if something looks off.',
      },
    ],
  },
  {
    id: 'payments',
    label: 'Payments',
    icon: BanknotesIcon,
    items: [
      {
        q: 'What payment methods do you accept?',
        a: 'Secure card at checkout, plus cash on delivery (COD) in Morocco where available.',
      },
      {
        q: 'What’s your return policy?',
        a: 'Contact support within 30 days—we’ll help with returns or replacements.',
      },
      {
        q: 'Is checkout secure?',
        a: 'Yes—SSL encrypted checkout and secure payment processing.',
      },
    ],
  },
] as const;

/** Flat list — derived from grouped FAQs. */
export const HOME_FAQ_ITEMS = HOME_FAQ_GROUPS.flatMap((group) => group.items);
